import React, { useMemo } from 'react';
import { Flex, Input, Select, Spin, Switch, Typography } from 'antd';
import {
  useGetTemplatesQuery,
  useGetWorksheetsQuery,
} from '@/state/slices/reports';
import { Template, Worksheet } from '@/models/template.ts';
import { Settings } from '@/models/settings';
import { Divider } from '@/shared/ui/Divider';
import { useFlag } from '@/hooks/useFlag';
import styles from './ReportingSection.module.css';

const { Text } = Typography;

interface ReportingSectionProps {
  settings: Settings;
  onUpdate: (partial: Partial<Settings>, debounceMs?: number) => void;
}

export const ReportingSection: React.FC<ReportingSectionProps> = ({
  settings,
  onUpdate,
}) => {
  const autosaveFlag = useFlag('autosave');
  const {
    data: templatesResp,
    isLoading: templateIsLoading,
    error: templatesError,
  } = useGetTemplatesQuery();

  const templates: Template[] = useMemo(
    () => templatesResp?.results ?? [],
    [templatesResp]
  );

  const templateOptions = useMemo(() => {
    const opts = templates.map((t: Template) => ({
      value: t.uuid,
      label: t.name ?? `Template ${t.uuid}`,
    }));
    return [{ value: '', label: 'None' }, ...opts];
  }, [templates]);

  const selectedTemplateName = useMemo(() => {
    if (!settings.defaultTemplate) return 'None';
    const found = templates.find(
      (t: Template) => t.uuid === settings.defaultTemplate
    );
    return found?.name ?? settings.defaultTemplate;
  }, [templates, settings.defaultTemplate]);

  const renderTemplateExtra = () => {
    if (templateIsLoading) {
      return (
        <span>
          <Spin size="small" /> Loading templates…
        </span>
      );
    }
    if (templatesError) {
      return (
        <span className={styles.errorText}>Could not load templates.</span>
      );
    }
    return (
      <span>
        Selected: <b>{selectedTemplateName}</b>
      </span>
    );
  };

  const {
    data: worksheetsResp,
    isLoading: worksheetIsLoading,
    error: worksheetsError,
  } = useGetWorksheetsQuery();

  const worksheets: Worksheet[] = useMemo(
    () => worksheetsResp?.results ?? [],
    [worksheetsResp]
  );

  const worksheetOptions = useMemo(() => {
    const opts = worksheets.map((w: Worksheet) => ({
      value: w.uuid,
      label: w.name ?? `Worksheet ${w.uuid}`,
    }));
    return [{ value: '', label: 'None' }, ...opts];
  }, [worksheets]);

  const selectedWorksheetName = useMemo(() => {
    if (!settings.defaultWorksheet) return 'None';
    const found = worksheets.find(
      (w: Worksheet) => w.uuid === settings.defaultWorksheet
    );
    return found?.name ?? settings.defaultWorksheet;
  }, [worksheets, settings.defaultWorksheet]);

  const renderWorksheetExtra = () => {
    if (worksheetIsLoading) {
      return (
        <span>
          <Spin size="small" /> Loading worksheets…
        </span>
      );
    }
    if (worksheetsError) {
      return (
        <span className={styles.errorText}>Could not load worksheets.</span>
      );
    }
    return (
      <span>
        Selected: <b>{selectedWorksheetName}</b>
      </span>
    );
  };

  return (
    <div className={styles.rows}>
      <div id="default-reporting-template" className={styles.field}>
        <Text strong>Default reporting template</Text>
        <Select
          allowClear
          placeholder="Select default template"
          options={templateOptions}
          loading={templateIsLoading}
          value={settings.defaultTemplate ?? ''}
          onChange={(value) => onUpdate({ defaultTemplate: value || '' })}
        />
        <Text type="secondary" className={styles.extra}>
          {renderTemplateExtra()}
        </Text>
      </div>

      <Divider />

      <div id="default-reader-worksheet" className={styles.field}>
        <Text strong>Default reader worksheet</Text>
        <Select
          allowClear
          placeholder="Select default worksheet"
          options={worksheetOptions}
          loading={worksheetIsLoading}
          value={settings.defaultWorksheet ?? ''}
          onChange={(value) => onUpdate({ defaultWorksheet: value || '' })}
        />
        <Text type="secondary" className={styles.extra}>
          {renderWorksheetExtra()}
        </Text>
      </div>

      <Divider />

      <div id="credentials-line" className={styles.field}>
        <Text strong>Credentials line</Text>
        <Input
          placeholder='e.g. "MD", "DO", "FACC"'
          maxLength={64}
          value={settings.credentialsLine ?? ''}
          onChange={(e) => onUpdate({ credentialsLine: e.target.value }, 600)}
        />
        <Text type="secondary" className={styles.extra}>
          Shown in the report signature block.
        </Text>
      </div>

      <Divider />

      {autosaveFlag && (
        <>
          <Flex id="autosave" align="center" justify="space-between">
            <Flex vertical>
              <Text strong>Autosave</Text>
              <Text type="secondary" className={styles.extra}>
                Automatically save the worksheet after changes.
              </Text>
            </Flex>
            <Switch
              checked={settings.autoSave}
              onChange={(checked) => onUpdate({ autoSave: checked })}
            />
          </Flex>

          <Divider />
        </>
      )}

      <Flex id="warn-unsaved-changes" align="center" justify="space-between">
        <Flex vertical>
          <Text strong>Warn unsaved changes</Text>
          <Text type="secondary" className={styles.extra}>
            Show a warning when navigating away with unsaved changes.
          </Text>
        </Flex>
        <Switch
          checked={settings.warnUnsavedChanges}
          onChange={(checked) => onUpdate({ warnUnsavedChanges: checked })}
        />
      </Flex>

      <Divider />

      <Flex id="imperial-units" align="center" justify="space-between">
        <Flex vertical>
          <Text strong>Imperial units</Text>
          <Text type="secondary" className={styles.extra}>
            Show height in feet/inches and weight in lbs. All other measurements
            stay metric.
          </Text>
        </Flex>
        <Switch
          checked={settings.unitSystem === 'IMPERIAL'}
          onChange={(checked) =>
            onUpdate({ unitSystem: checked ? 'IMPERIAL' : 'METRIC' })
          }
        />
      </Flex>

      <Divider />

      <div className={styles.field}>
        <Text strong>Report section order</Text>
        <Input disabled placeholder="Report section ordering (coming soon)." />
      </div>
    </div>
  );
};
